import React from 'react';

import rafSchedule from 'raf-schd';
import type { IntlShape } from 'react-intl';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import ReactNodeView from '@atlaskit/editor-common/react-node-view';
import type { getInlineNodeViewProducer } from '@atlaskit/editor-common/react-node-view';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent, UnsupportedBlock } from '@atlaskit/editor-common/ui';
import { canRenderDatasource } from '@atlaskit/editor-common/utils';
import type { EditorViewModePluginState } from '@atlaskit/editor-plugin-editor-viewmode';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type {
	Decoration,
	DecorationSource,
	EditorView,
	NodeView,
} from '@atlaskit/editor-prosemirror/view';
import {
	SmartLinkDraggable,
	SMART_LINK_DRAG_TYPES,
	SMART_LINK_APPEARANCE,
} from '@atlaskit/editor-smart-link-draggable';
import type { CardContext } from '@atlaskit/link-provider/types';
import { Card as SmartCard } from '@atlaskit/smart-card';
import { CardSSR } from '@atlaskit/smart-card/ssr';

import { Datasource } from '../nodeviews/datasource';
import { registerCard, removeCard } from '../pm-plugins/actions';
import { isDatasourceNode } from '../pm-plugins/utils';
import { SmartCardSSRReactContextsProvider } from '../ui/SmartCardSSRReactContextsProvider';
import type { SmartCardProps } from './genericCard';
import { Card } from './genericCard';

// eslint-disable-next-line @repo/internal/react/no-class-components
export class BlockCardComponent extends React.PureComponent<
	SmartCardProps & { id?: string },
	unknown
> {
	private scrollContainer?: HTMLElement;

	constructor(props: SmartCardProps & { id?: string }) {
		super(props);
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/editor/no-as-casting
		this.scrollContainer = findOverflowScrollParent(props.view.dom as HTMLElement) || undefined;
	}

	onResolve = (data: { title?: string; url?: string }): void => {
		const { getPos, view } = this.props;
		if (!getPos || typeof getPos === 'boolean') {
			return;
		}

		const { title, url } = data;

		// don't dispatch immediately since we might be in the middle of
		// rendering a nodeview
		rafSchedule(() => {
			const pos = getPos();

			if (typeof pos !== 'number') {
				return;
			}

			view.dispatch(
				registerCard({
					title,
					url,
					pos,
					id: this.props.id,
				})(view.state.tr),
			);
		})();
	};

	componentWillUnmount(): void {
		this.removeCard();
	}

	private removeCardDispatched = false;

	private removeCard() {
		if (this.removeCardDispatched) {
			return;
		}
		this.removeCardDispatched = true;
		const { tr } = this.props.view.state;
		removeCard({ id: this.props.id })(tr);
		this.props.view.dispatch(tr);
	}

	gapCursorSpan = (): React.JSX.Element | undefined => {
		const browser = getBrowserInfo();
		// Don't render in EdgeHTMl version <= 18 (Edge version 44)
		// as it forces the edit popup to render 24px lower than it should
		if (browser.ie && browser.ie_version < 79) {
			return;
		}

		// render an empty span afterwards to get around Webkit bug
		// that puts caret in next editable text element
		return <span contentEditable={true} />;
	};

	onError = ({ err }: { err?: Error }): void => {
		if (err) {
			throw err;
		}
	};

	render(): React.JSX.Element {
		const { node, cardContext, actionOptions, onClick, CompetitorPrompt, isPageSSRed } = this.props;
		const { url, data } = node.attrs;

		const cardInner = isPageSSRed ? (
			<>
				<CardSSR
					key={url}
					url={url ?? data.url}
					container={this.scrollContainer}
					appearance="block"
					onClick={onClick}
					onResolve={this.onResolve}
					onError={this.onError}
					platform={'web'}
					actionOptions={actionOptions}
					CompetitorPrompt={CompetitorPrompt}
					hideIconLoadingSkeleton={true}
				/>
				{this.gapCursorSpan()}
			</>
		) : (
			<>
				<SmartCard
					key={url}
					url={url ?? data.url}
					container={this.scrollContainer}
					appearance="block"
					onClick={onClick}
					onResolve={this.onResolve}
					onError={this.onError}
					platform={'web'}
					actionOptions={actionOptions}
					CompetitorPrompt={CompetitorPrompt}
				/>
				{this.gapCursorSpan()}
			</>
		);
		// [WS-2307]: we only render card wrapped into a Provider when the value is ready,
		// otherwise if we got data, we can render the card directly since it doesn't need the Provider
		return (
			<SmartLinkDraggable
				url={url}
				appearance={SMART_LINK_APPEARANCE.BLOCK}
				source={SMART_LINK_DRAG_TYPES.EDITOR}
			>
				<div>
					{cardContext && cardContext.value ? (
						<cardContext.Provider value={cardContext.value}>{cardInner}</cardContext.Provider>
					) : data ? (
						cardInner
					) : null}
				</div>
			</SmartLinkDraggable>
		);
	}
}

const WrappedBlockCard = Card(BlockCardComponent, UnsupportedBlock);

export type BlockCardNodeViewProps = Pick<
	SmartCardProps,
	| 'actionOptions'
	| 'pluginInjectionApi'
	| 'onClickCallback'
	| 'isPageSSRed'
	| 'provider'
	| 'CompetitorPrompt'
	| 'intl'
	| 'smartCardContext'
>;

export class BlockCard extends ReactNodeView<BlockCardNodeViewProps> {
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	private id = uuid();

	unsubscribe: (() => void) | undefined;

	createDomRef(): HTMLElement {
		const domRef = document.createElement('div');
		// workaround Chrome bug in https://product-fabric.atlassian.net/browse/ED-5379
		// see also: https://github.com/ProseMirror/prosemirror/issues/884
		this.unsubscribe =
			this.reactComponentProps.pluginInjectionApi?.editorViewMode?.sharedState.onChange(
				({ nextSharedState }) => this.updateContentEditable(nextSharedState, domRef),
			);
		this.updateContentEditable(
			this.reactComponentProps.pluginInjectionApi?.editorViewMode?.sharedState.currentState(),
			domRef,
		);
		domRef.setAttribute('spellcheck', 'false');
		return domRef;
	}

	private updateContentEditable = (
		editorViewModeState: EditorViewModePluginState | null | undefined,
		divElement: HTMLDivElement,
	) => {
		divElement.contentEditable = editorViewModeState?.mode === 'view' ? 'false' : 'true';
	};

	// Need this function to check if the datasource attribute was added or not to a blockCard.
	// If so, we return false so we can get the node to re-render properly as a datasource node instead.
	// Otherwise, the node view will still consider the node as a blockCard and render a regular blockCard.
	validUpdate(currentNode: Node, newNode: Node): boolean {
		const isCurrentNodeBlockCard = !isDatasourceNode(currentNode);
		const isNewNodeDatasource = isDatasourceNode(newNode);

		// need to return falsy to update node
		return !(isCurrentNodeBlockCard && isNewNodeDatasource);
	}

	update(
		node: Node,
		decorations: ReadonlyArray<Decoration>,
		_innerDecorations?: DecorationSource,
	): boolean {
		return super.update(node, decorations, _innerDecorations, this.validUpdate);
	}

	render(): React.JSX.Element {
		const {
			actionOptions,
			pluginInjectionApi,
			onClickCallback,
			CompetitorPrompt,
			isPageSSRed,
			provider,
			intl,
			smartCardContext,
		} = this.reactComponentProps;

		return (
			<SmartCardSSRReactContextsProvider intl={intl} smartCardContext={smartCardContext}>
				<WrappedBlockCard
					node={this.node}
					view={this.view}
					getPos={this.getPos}
					actionOptions={actionOptions}
					pluginInjectionApi={pluginInjectionApi}
					onClickCallback={onClickCallback}
					id={this.id}
					CompetitorPrompt={CompetitorPrompt}
					isPageSSRed={isPageSSRed}
					provider={provider}
				/>
			</SmartCardSSRReactContextsProvider>
		);
	}

	/**
	 * Prevent ProseMirror from handling drag events on the smart-element-link,
	 * allowing native drag to work so SmartLinkDraggable can intercept it.
	 * @see {@link https://prosemirror.net/docs/ref/#view.NodeView.stopEvent}
	 */
	stopEvent(event: Event): boolean {
		if (event.type === 'dragstart') {
			const target = event.target;
			if (target instanceof HTMLElement && target.closest('[data-smart-element-link]')) {
				return true;
			}
		}
		return false;
	}

	destroy(): void {
		this.unsubscribe?.();
		super.destroy();
	}
}

export interface BlockCardNodeViewProperties {
	actionOptions: BlockCardNodeViewProps['actionOptions'];
	allowDatasource: boolean | undefined;
	CompetitorPrompt?: React.ComponentType<{ linkType?: string; sourceUrl: string }>;
	inlineCardViewProducer: ReturnType<typeof getInlineNodeViewProducer>;
	intl?: IntlShape;
	isPageSSRed: BlockCardNodeViewProps['isPageSSRed'];
	onClickCallback: BlockCardNodeViewProps['onClickCallback'];
	pluginInjectionApi: BlockCardNodeViewProps['pluginInjectionApi'];
	pmPluginFactoryParams: PMPluginFactoryParams;
	provider: BlockCardNodeViewProps['provider'];
	smartCardContext?: CardContext;
}

export const blockCardNodeView =
	({
		pmPluginFactoryParams,
		actionOptions,
		pluginInjectionApi,
		onClickCallback,
		allowDatasource,
		inlineCardViewProducer,
		CompetitorPrompt,
		isPageSSRed,
		provider,
		intl,
		smartCardContext,
	}: BlockCardNodeViewProperties) =>
	(
		node: Node,
		view: EditorView,
		getPos: () => number | undefined,
		decorations: readonly Decoration[],
	): BlockCard | Datasource | NodeView => {
		const { portalProviderAPI, eventDispatcher } = pmPluginFactoryParams;
		const reactComponentProps: BlockCardNodeViewProps = {
			actionOptions,
			pluginInjectionApi,
			onClickCallback: onClickCallback,
			CompetitorPrompt,
			isPageSSRed,
			provider,
			intl,
			smartCardContext,
		};
		const isDatasource = isDatasourceNode(node);

		if (isDatasource) {
			if (allowDatasource && canRenderDatasource(node?.attrs?.datasource?.id)) {
				const datasourcePosition = typeof getPos === 'function' && getPos();

				const datasourceResolvedPosition =
					datasourcePosition && view.state.doc.resolve(datasourcePosition);

				const isNodeNested = !!(datasourceResolvedPosition && datasourceResolvedPosition.depth > 0);

				return new Datasource({
					node,
					view,
					getPos,
					portalProviderAPI,
					eventDispatcher,
					pluginInjectionApi,
					isNodeNested,
				}).init();
			} else {
				return inlineCardViewProducer(node, view, getPos, decorations);
			}
		}

		return new BlockCard(
			node,
			view,
			getPos,
			portalProviderAPI,
			eventDispatcher,
			reactComponentProps,
			undefined,
		).init();
	};
